// Authentication context for managing user state
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../apiClient';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

    // Check if user is authenticated on app start
    useEffect(() => {
        // Only check auth once per session
        if (!hasCheckedAuth) {
            checkAuth();
        } else {
            // Already checked auth, just set loading to false
            setLoading(false);
        }
    }, [hasCheckedAuth]);

    const checkAuth = async () => {
        try {
            setLoading(true);
            
            // Check for LINE token in URL parameters first
            const urlParams = new URLSearchParams(window.location.search);
            const lineToken = urlParams.get('token');
            
            if (lineToken) {
                console.log('LINE token found in URL, exchanging for full authentication');
                try {
                    // Exchange LINE token for full authentication
                    const response = await apiClient.post('/auth/line/exchange', { token: lineToken });
                    console.log('LINE token exchange response:', response.data);
                    setUser(response.data);
                    
                    // Remove token from URL to clean up
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.delete('token');
                    window.history.replaceState({}, '', newUrl);
                    
                    return;
                } catch (error) {
                    console.error('LINE token exchange failed:', error);
                    // Fall through to regular auth check
                }
            }
            
            // Debug: Check cookies before making request
            console.log('Cookies before /auth/me request:', document.cookie);
            
            const response = await apiClient.get('/auth/me');
            console.log('Auth response:', response);
            console.log('Auth response.data:', response.data);
            console.log('Auth response.data type:', typeof response.data);
            console.log('Auth response.data keys:', Object.keys(response.data || {}));
            
            // Check if response.data is empty object
            if (response.data && typeof response.data === 'object' && Object.keys(response.data).length === 0) {
                console.warn('Received empty user object from /auth/me - checking localStorage for cached user');
                
                // Check if we have cached user data in localStorage (temporary workaround)
                const cachedUser = localStorage.getItem('cachedUser');
                if (cachedUser) {
                    try {
                        const userData = JSON.parse(cachedUser);
                        console.log('Using cached user data:', userData);
                        setUser(userData);
                        return;
                    } catch (e) {
                        console.error('Failed to parse cached user data:', e);
                        localStorage.removeItem('cachedUser');
                    }
                }
                
                console.log('Response headers:', response.headers);
                console.log('Response status:', response.status);
            }
            
            setUser(response.data);

            // Cache user data to localStorage for analytics.js getUserId()
            if (response.data && Object.keys(response.data).length > 0) {
                localStorage.setItem('cachedUser', JSON.stringify(response.data));
            }
        } catch (error) {
            console.log('User not authenticated:', error.response?.status);
            console.log('Error details:', error.response?.data);
            setUser(null);
            // Only redirect to login if we're on a protected page (not on login page)
            const protectedPaths = ['/trackList', '/addTrack', '/editTrack', '/notify_history'];
            const currentPath = window.location.pathname;
            if (protectedPaths.includes(currentPath) && currentPath !== '/login') {
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
            setHasCheckedAuth(true); // Mark that we've checked auth
        }
    };

    const login = () => {
        // Redirect to Google OAuth
        console.log('Redirecting to Google OAuth');
        console.log(`${process.env.REACT_APP_API_ROOT}/api/my_shopper/auth/google`);
        window.location.href = `${process.env.REACT_APP_API_ROOT}/api/my_shopper/auth/google`;
    };

    const loginWithTelegram = async (telegramUser) => {
        // Send Telegram user data to backend for verification
        console.log('Sending Telegram user data to backend:', telegramUser);
        try {
            const response = await apiClient.post('/auth/telegram', telegramUser);
            console.log('Telegram login response:', response.data);

            // If backend returns token in response, store it
            if (response.data.token) {
                localStorage.setItem('accessToken', response.data.token);
            }

            // Set user data
            setUser(response.data.user || response.data);

            // Cache user data
            localStorage.setItem('cachedUser', JSON.stringify(response.data.user || response.data));

            return response.data;
        } catch (error) {
            console.error('Telegram login failed:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await apiClient.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            setHasCheckedAuth(false); // Reset auth check flag so it can be checked again
            // Clear all user data from localStorage
            localStorage.removeItem('cachedUser');
            localStorage.removeItem('accessToken');
            sessionStorage.removeItem('telegramAuthProcessed');

            // If opened in Telegram Web App, close the window
            if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.close();
            } else {
                // Otherwise redirect to login page
                window.location.href = '/login';
            }
        }
    };

    const value = {
        user,
        loading,
        login,
        loginWithTelegram,
        logout,
        checkAuth,
        hasCheckedAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

